import React, { useState, useEffect } from 'react';
import {
    Button, Card, CardBody, Spinner, Typography,
    Dialog, DialogHeader, DialogBody, DialogFooter, Chip,
} from '@material-tailwind/react';
import { Plus, Edit2, Trash2, Building, AlertTriangleIcon, Save, FileText } from 'lucide-react';
import {
    useGetBanks, useAddBank, useUpdateBank, useDeleteBank,
} from '../../../custom-hooks/use-banks';

import { Notify } from '../../../utils/functions';
import { useConfirm } from '../../../custom-hooks/confirm-dialog';
import { GetInvoiceSettings, UpdateInvoiceSettings } from '../../../services/backend';
import { useAuth } from '../../../services/contexts/authContext';
import { STATIC_USER_ROLES } from '../../../utils/enums';

const emptyForm = {
    name: '', accountTitle: '', accountNumber: '', iban: '',
    branchName: '', swiftCode: '', notes: '', isActive: true,
};

const SettingsPayments = () => {
    const { data: banks = [], isLoading } = useGetBanks();
    const addMutation = useAddBank();
    const updateMutation = useUpdateBank();
    const deleteMutation = useDeleteBank();
    const { confirm, ConfirmationDialog } = useConfirm();

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);

    // Invoice content settings
    const [invoiceSettings, setInvoiceSettings] = useState({ notes: '', termsAndConditions: '' });
    const [invoiceSettingsLoading, setInvoiceSettingsLoading] = useState(false);
    const [invoiceSettingsSaving, setInvoiceSettingsSaving] = useState(false);
    const [invoiceSettingsLoaded, setInvoiceSettingsLoaded] = useState(false);

    const { userDetails } = useAuth();
    const hasAccess = userDetails?.role === STATIC_USER_ROLES.SUPER_ADMIN || 
                      userDetails?.role === STATIC_USER_ROLES.ADMIN ||
                      (userDetails?.role === STATIC_USER_ROLES.STAFF && userDetails?.dynamicRole?.permissions?.includes('FX22-3:manage'));

    // Load invoice content settings on mount
    useEffect(() => {
        if (!hasAccess) return;
        setInvoiceSettingsLoading(true);
        GetInvoiceSettings()
            .then(res => {
                const s = res.data?.settings || {};
                setInvoiceSettings({
                    notes: s.notes || '',
                    termsAndConditions: s.termsAndConditions || '',
                });
                setInvoiceSettingsLoaded(true);
            })
            .catch(() => setInvoiceSettingsLoaded(true))
            .finally(() => setInvoiceSettingsLoading(false));
    }, [hasAccess]);

    const openCreate = () => {
        setForm(emptyForm);
        setEditingId(null);
        setDialogOpen(true);
    };

    const openEdit = (bank) => {
        setForm({
            name: bank.name || '',
            accountTitle: bank.accountTitle || '',
            accountNumber: bank.accountNumber || '',
            iban: bank.iban || '',
            branchName: bank.branchName || '',
            swiftCode: bank.swiftCode || '',
            notes: bank.notes || '',
            isActive: bank.isActive !== false,
        });
        setEditingId(bank._id);
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (!form.name?.trim()) {
            Notify('Bank name is required', 'error');
            return;
        }
        try {
            if (editingId) {
                await updateMutation.mutateAsync({ bankId: editingId, data: form });
                Notify('Bank updated', 'success');
            } else {
                await addMutation.mutateAsync(form);
                Notify('Bank added', 'success');
            }
            setDialogOpen(false);
        } catch (error) {
            Notify(error?.response?.data?.message || 'Failed to save bank', 'error');
        }
    };

    const handleDelete = async (bank) => {
        const isConfirmed = await confirm({
            title: 'Delete Bank',
            message: `Delete "${bank.name}"? This cannot be undone.`,
            confirmText: 'Delete',
            cancelText: 'Cancel',
            confirmBtnColor: 'red',
        });
        if (!isConfirmed) return;
        try {
            await deleteMutation.mutateAsync(bank._id);
            Notify('Bank deleted', 'success');
        } catch (error) {
            Notify('Failed to delete bank', 'error');
        }
    };

    if (!hasAccess) {
        return (
            <div className='flex min-h-[60vh] flex-col items-center justify-center p-8'>
                <div className='rounded-full bg-blue-gray-50 p-6'>
                    <AlertTriangleIcon size={48} className='text-blue-gray-300' />
                </div>
                <h2 className='mt-6 text-2xl font-bold text-blue-gray-900'>Access Denied</h2>
                <p className='mt-2 text-center text-blue-gray-500 max-w-sm'>
                    You don't have permission to view or manage Payment Settings.
                </p>
            </div>
        );
    }

    return (
        <div className='p-4 md:p-8 space-y-8'>

            {/* ── Section 1: Bank Accounts ─────────────────────────────────────── */}
            <Card className='border border-blue-gray-100 shadow-sm'>
                <CardBody className='p-6'>
                    {/* Section Header */}
                    <div className='flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8'>
                        <div>
                            <p className='text-xs uppercase tracking-[0.2em] text-blue-gray-400 font-medium'>Payments</p>
                            <h1 className='mt-1 text-2xl font-bold text-blue-gray-900'>Bank Accounts</h1>
                            <p className='mt-2 text-sm text-blue-gray-500 max-w-lg'>
                                Manage bank accounts that can be selected when recording bank transfer payments on invoices.
                            </p>
                        </div>
                        <Button
                            variant='gradient' color='gray' size='sm'
                            className='flex items-center gap-2 rounded-full self-start shrink-0'
                            onClick={openCreate}
                        >
                            <Plus className='h-4 w-4' /> Add Bank
                        </Button>
                    </div>

                    {/* Bank List */}
                    {isLoading ? (
                        <div className='flex justify-center items-center min-h-[20vh]'>
                            <Spinner />
                        </div>
                    ) : banks.length === 0 ? (
                        <div className='rounded-2xl border-2 border-dashed border-blue-gray-100 flex flex-col items-center justify-center py-12 text-center'>
                            <div className='rounded-full bg-blue-gray-50 p-4 mb-4'>
                                <Building className='h-8 w-8 text-blue-gray-300' />
                            </div>
                            <Typography variant='h6' color='gray'>No banks added yet</Typography>
                            <Typography variant='small' className='text-gray-400 mt-1 max-w-xs'>
                                Add a bank to start selecting it when recording bank transfer payments.
                            </Typography>
                            <Button
                                variant='outlined' color='gray' size='sm'
                                className='mt-5 flex items-center gap-1 rounded-full'
                                onClick={openCreate}
                            >
                                <Plus className='h-3.5 w-3.5' /> Add First Bank
                            </Button>
                        </div>
                    ) : (
                        <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5'>
                            {banks.map((bank) => (
                                <div
                                    key={bank._id}
                                    className='rounded-xl border border-blue-gray-100 bg-white shadow-sm hover:shadow-md transition-shadow overflow-hidden flex flex-col'
                                >
                                    {/* Card top accent */}
                                    <div className='h-1.5 w-full bg-gradient-to-r from-blue-gray-700 to-blue-gray-400' />

                                    <div className='p-5 flex flex-col flex-1'>
                                    {/* Name + status */}
                                    <div className='flex items-start justify-between mb-4'>
                                        <div className='flex items-center gap-3'>
                                            <div className='rounded-xl bg-blue-gray-50 p-2.5'>
                                                <Building className='h-5 w-5 text-blue-gray-600' />
                                            </div>
                                            <div>
                                                <div className='font-bold text-blue-gray-900 text-sm leading-tight'>{bank.name}</div>
                                                {bank.accountTitle && (
                                                    <div className='text-xs text-blue-gray-400 mt-0.5'>{bank.accountTitle}</div>
                                                )}
                                            </div>
                                        </div>
                                        <Chip
                                            value={bank.isActive ? 'Active' : 'Inactive'}
                                            color={bank.isActive ? 'green' : 'gray'}
                                            size='sm'
                                            variant='ghost'
                                            className='text-[10px] uppercase font-bold rounded-full shrink-0'
                                        />
                                    </div>

                                    {/* Account details */}
                                    <div className='rounded-xl bg-blue-gray-50/60 px-4 py-3 space-y-2 mb-4 flex-1'>
                                        {bank.accountNumber && (
                                            <div className='flex justify-between text-xs'>
                                                <span className='text-blue-gray-400 font-medium'>Account #</span>
                                                <span className='text-blue-gray-700 font-semibold tracking-wide'>{bank.accountNumber}</span>
                                            </div>
                                        )}
                                        {bank.iban && (
                                            <div className='flex justify-between text-xs'>
                                                <span className='text-blue-gray-400 font-medium'>IBAN</span>
                                                <span className='text-blue-gray-700 font-semibold tracking-wide'>{bank.iban}</span>
                                            </div>
                                        )}
                                        {bank.branchName && (
                                            <div className='flex justify-between text-xs'>
                                                <span className='text-blue-gray-400 font-medium'>Branch</span>
                                                <span className='text-blue-gray-700 font-semibold'>{bank.branchName}</span>
                                            </div>
                                        )}
                                        {bank.swiftCode && (
                                            <div className='flex justify-between text-xs'>
                                                <span className='text-blue-gray-400 font-medium'>SWIFT</span>
                                                <span className='text-blue-gray-700 font-semibold'>{bank.swiftCode}</span>
                                            </div>
                                        )}
                                        {!bank.accountNumber && !bank.iban && !bank.branchName && !bank.swiftCode && (
                                            <p className='text-xs text-blue-gray-300 text-center py-1'>No details added</p>
                                        )}
                                    </div>

                                    {/* Actions */}
                                    <div className='flex gap-2 mt-auto'>
                                        <Button
                                            variant='outlined' color='gray' size='sm'
                                            className='flex-1 flex items-center justify-center gap-1 rounded-full text-xs'
                                            onClick={() => openEdit(bank)}
                                        >
                                            <Edit2 className='h-3 w-3' /> Edit
                                        </Button>
                                        <Button
                                            variant='outlined' color='red' size='sm'
                                            className='flex-1 flex items-center justify-center gap-1 rounded-full text-xs'
                                            onClick={() => handleDelete(bank)}
                                        >
                                            <Trash2 className='h-3 w-3' /> Delete
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                </CardBody>
            </Card>


            {/* ── Section 2: Invoice Content ────────────────────────────────────── */}
            <Card className='border border-blue-gray-100 shadow-sm'>
                <CardBody className='p-6'>
                    <div className='flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8'>
                        <div>
                            <p className='text-xs uppercase tracking-[0.2em] text-blue-gray-400 font-medium'>Invoices</p>
                            <h2 className='mt-1 text-2xl font-bold text-blue-gray-900'>Invoice Content</h2>
                            <p className='mt-2 text-sm text-blue-gray-500'>
                                Customize the Notes and Terms &amp; Conditions that appear on every invoice PDF.
                            </p>
                        </div>
                        <FileText className='h-8 w-8 text-blue-gray-300 flex-shrink-0' />
                    </div>

                    {invoiceSettingsLoading ? (
                        <div className='flex justify-center items-center min-h-[15vh]'><Spinner /></div>
                    ) : invoiceSettingsLoaded ? (
                        <div className='space-y-5'>
                            <div>
                                <label className='text-sm font-semibold text-blue-gray-700 mb-1 block'>Notes</label>
                                <p className='text-xs text-blue-gray-400 mb-2'>
                                    Appears in the "Notes" section of every PDF. Include bank details, payment instructions, etc.
                                </p>
                                <textarea
                                    rows={7}
                                    className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono'
                                    value={invoiceSettings.notes}
                                    onChange={(e) => setInvoiceSettings(prev => ({ ...prev, notes: e.target.value }))}
                                    placeholder={'e.g. Thanks for your business.\n\nBank Details:\nBank Name\nIBAN\nNOTE: Please share payment proof once payment is done.'}
                                />
                            </div>
                            <div>
                                <label className='text-sm font-semibold text-blue-gray-700 mb-1 block'>Terms &amp; Conditions</label>
                                <p className='text-xs text-blue-gray-400 mb-2'>
                                    Appears in the "Terms &amp; Conditions" section of every PDF.
                                </p>
                                <textarea
                                    rows={4}
                                    className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono'
                                    value={invoiceSettings.termsAndConditions}
                                    onChange={(e) => setInvoiceSettings(prev => ({ ...prev, termsAndConditions: e.target.value }))}
                                    placeholder='e.g. All payments are non-refundable.'
                                />
                            </div>
                            <div className='flex justify-end'>
                                <Button
                                    variant='gradient' color='gray' size='sm'
                                    className='flex items-center gap-2 rounded-full'
                                    disabled={invoiceSettingsSaving}
                                    onClick={async () => {
                                        try {
                                            setInvoiceSettingsSaving(true);
                                            await UpdateInvoiceSettings(invoiceSettings);
                                            Notify('Invoice content settings saved', 'success');
                                        } catch {
                                            Notify('Failed to save invoice content settings', 'error');
                                        } finally {
                                            setInvoiceSettingsSaving(false);
                                        }
                                    }}
                                >
                                    {invoiceSettingsSaving ? <Spinner className='h-4 w-4' /> : <Save className='h-4 w-4' />}
                                    Save Content Settings
                                </Button>
                            </div>
                        </div>
                    ) : null}
                </CardBody>
            </Card>

            <Dialog open={dialogOpen} handler={() => setDialogOpen(false)} size='sm'>
                <DialogHeader>{editingId ? 'Edit Bank' : 'Add Bank'}</DialogHeader>
                <DialogBody className='space-y-3 max-h-[70vh] overflow-y-auto'>
                    <div>
                        <label className='text-sm text-gray-600 mb-1 block'>Bank Name *</label>
                        <input
                            type='text'
                            className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
                            value={form.name}
                            onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                            placeholder='e.g. Meezan Bank'
                        />
                    </div>
                    <div>
                        <label className='text-sm text-gray-600 mb-1 block'>Account Title</label>
                        <input
                            type='text'
                            className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
                            value={form.accountTitle}
                            onChange={(e) => setForm(prev => ({ ...prev, accountTitle: e.target.value }))}
                            placeholder='Workbench Pvt Ltd'
                        />
                    </div>
                    <div className='grid grid-cols-2 gap-3'>
                        <div>
                            <label className='text-sm text-gray-600 mb-1 block'>Account Number</label>
                            <input
                                type='text'
                                className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
                                value={form.accountNumber}
                                onChange={(e) => setForm(prev => ({ ...prev, accountNumber: e.target.value }))}
                            />
                        </div>
                        <div>
                            <label className='text-sm text-gray-600 mb-1 block'>IBAN</label>
                            <input
                                type='text'
                                className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
                                value={form.iban}
                                onChange={(e) => setForm(prev => ({ ...prev, iban: e.target.value }))}
                            />
                        </div>
                    </div>
                    <div className='grid grid-cols-2 gap-3'>
                        <div>
                            <label className='text-sm text-gray-600 mb-1 block'>Branch Name</label>
                            <input
                                type='text'
                                className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
                                value={form.branchName}
                                onChange={(e) => setForm(prev => ({ ...prev, branchName: e.target.value }))}
                            />
                        </div>
                        <div>
                            <label className='text-sm text-gray-600 mb-1 block'>SWIFT Code</label>
                            <input
                                type='text'
                                className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
                                value={form.swiftCode}
                                onChange={(e) => setForm(prev => ({ ...prev, swiftCode: e.target.value }))}
                            />
                        </div>
                    </div>
                    <div>
                        <label className='text-sm text-gray-600 mb-1 block'>Notes</label>
                        <textarea
                            rows={2}
                            className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
                            value={form.notes}
                            onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                        />
                    </div>
                    <label className='flex items-center gap-2 text-sm text-gray-700'>
                        <input
                            type='checkbox'
                            className='h-4 w-4 accent-blue-600'
                            checked={form.isActive}
                            onChange={(e) => setForm(prev => ({ ...prev, isActive: e.target.checked }))}
                        />
                        Active (available for selection on payment forms)
                    </label>
                </DialogBody>
                <DialogFooter>
                    <Button variant='text' onClick={() => setDialogOpen(false)} className='mr-2'>Cancel</Button>
                    <Button
                        variant='gradient' color='gray'
                        onClick={handleSave}
                        disabled={addMutation.isLoading || updateMutation.isLoading}
                        className='flex items-center gap-2'
                    >
                        {(addMutation.isLoading || updateMutation.isLoading)
                            ? <Spinner className='h-4 w-4' />
                            : <Save className='h-4 w-4' />}
                        {editingId ? 'Save Changes' : 'Add Bank'}
                    </Button>
                </DialogFooter>
            </Dialog>
            <ConfirmationDialog />
        </div>
    );
};

export default SettingsPayments;
